import {
  type ActionType,
  IconSection,
} from "Components/Shared/BanksList/BankButton/Components/IconSection";
import { NotRecommendedBadge } from "Components/Shared/BanksList/BankButton/Components/NotRecommendedBadge";
import { StarRating } from "Components/Shared/BanksList/BankButton/Components/StarRating";
import type { Bank } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { SandboxText } from "Components/Shared/Bank/SandboxText";
import { BankImage } from "Components/Shared/BankImage";
import { BankRatingDetailModal } from "Components/Shared/BanksList/BankButton/BankRatingDetailModal";
import { NotRecommendedWarningModal } from "Components/Shared/BanksList/BankButton/NotRecommendedWarningModal";

const BankNameResources = Resources.BankNames;
const BankRatingResources = Resources.BankRating.Banks;

type Props = {
  bank: Bank;
  isDisabled?: boolean;
  isSandbox?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  actionType?: ActionType;
};

export const BankButton = ({
  isDisabled,
  bank,
  isSandbox,
  onClick,
  isLoading,
  actionType,
}: Props) => {
  const [isRatingOpen, setIsRatingOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const { t, exists } = useResource();
  const isMerchant = useIsCurrentUserMerchant();

  const isButtonDisabled = isDisabled || isLoading;
  const bankName = t(BankNameResources[bank]);
  const isNotRecommended =
    !isMerchant &&
    actionType !== "reauth" &&
    exists(BankRatingResources[bank].NotRecommended?.Reason);
  const rating =
    t(BankRatingResources[bank].Rating) === ""
      ? undefined
      : Number(t(BankRatingResources[bank].Rating)) || 0;

  const handleActionClick = () => {
    if (isNotRecommended) {
      return setIsWarningOpen(true);
    }
    return onClick?.();
  };

  const handleConfirmRiskyConnection = () => {
    setIsWarningOpen(false);
    onClick?.();
  };

  return (
    <Box
      display="flex"
      sx={({ palette }) => ({
        border: `1px solid ${palette.grey[200]}`,
        borderRadius: "10px",
        overflow: "hidden",
        opacity: isButtonDisabled ? "0.45" : "1",
        backgroundColor: isButtonDisabled ? palette.action.hover : "inherit",
      })}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={4}
        ml={3}
        py={3}
        flexGrow={1}
        minHeight="60px"
      >
        <BankImage bank={bank} name={bankName} isGrayscale={isDisabled} />
        <Box
          display="flex"
          flexDirection={["column", "row"]}
          flexWrap="wrap"
          justifyContent="space-between"
          gap={1}
          width="100%"
        >
          <Typography fontWeight={400}>
            {bankName} {isSandbox && <SandboxText />}
          </Typography>
          {!isDisabled && (isNotRecommended || rating !== undefined) && (
            <Box
              component="button"
              onClick={() => setIsRatingOpen(true)}
              disabled={isButtonDisabled}
              ml={[0, 2]}
              mr={[2, 4]}
              type="button"
              sx={{ all: "unset", cursor: "pointer" }}
              aria-label={t(Resources.BankRating.AccessibilityLabel, {
                bankName,
              })}
              aria-haspopup="dialog"
              aria-expanded={isRatingOpen ? "true" : "false"}
            >
              {isNotRecommended ? (
                <NotRecommendedBadge />
              ) : (
                <StarRating rating={rating!} showInfoIcon />
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box>
        <IconSection
          isDisabled={isDisabled}
          isLoading={isLoading}
          actionType={actionType}
          isNotRecommended={isNotRecommended}
          onClick={handleActionClick}
        />
      </Box>
      <BankRatingDetailModal
        isDialogOpen={isRatingOpen}
        handleCloseRating={() => setIsRatingOpen(false)}
        bank={bank}
      />
      {isNotRecommended && (
        <NotRecommendedWarningModal
          isOpen={isWarningOpen}
          onClose={() => setIsWarningOpen(false)}
          onConfirm={handleConfirmRiskyConnection}
          bank={bank}
        />
      )}
    </Box>
  );
};
